import { Icon, IconProps } from '../atoms';
import {
  AvatarNew,
  AvatarProps,
  Button,
  PillBadge,
  PillBadgeColors,
  StandardButtonProps,
} from '../molecules';
import { ReactNode } from 'react';

export type CardBadge = {
  text: string;
  color: PillBadgeColors;
};

export type CardProps = (
  | {
      variant: 'large';
      footerText?: string;
      footerLinkText?: string;
      footerLinkHref?: string;
      footerLinkOnClick?: () => void;

      contentSubtextHref: string;
    }
  | {
      variant: 'small';
    }
) & {
  headerContent: ReactNode;
  headerColor: string;

  badges: CardBadge[];
  buttonText?: string;
  buttonVariant?: StandardButtonProps['variant'];
  buttonDisabled?: boolean;
  buttonOnClick?: () => void;
  buttonHref?: string;

  avatarSize: AvatarProps['size'];

  content: React.ReactNode;
  contentIcon?: IconProps['type'];
  contentSubText: string;

  contentTextHref?: string;
  avatarHref?: string;
  /* Whether to present the avatar as for a user or an org. Org avatars
   * have padding and a different fallback icon */
  userOrOrg?: 'user' | 'organization';

  isLoading?: boolean;

  dataTestId?: string;
};

export const Card = (props: CardProps) => {
  const {
    headerContent,
    content,
    contentIcon,
    buttonVariant,
    contentSubText,
    avatarHref,
    contentTextHref,
    buttonDisabled,
    badges,
    headerColor,
    buttonText,
    buttonOnClick,
    buttonHref,
    userOrOrg,
    dataTestId,
  } = { ...props };

  const badgeRow = (
    <div className='flex justify-center md:justify-normal flex-row gap-2'>
      {badges.map((badge) => {
        return (
          <PillBadge key={badge.text} variant='plain' size='sm' color={badge.color}>
            {badge.text}
          </PillBadge>
        );
      })}
    </div>
  );

  return (
    <div
      className={`md:w-full w-64 ${
        props.variant === 'large' ? 'md:max-h-[185px] shadow-md' : 'md:max-h-[124px]'
      } rounded-xl border max-w-[880px] border-gray-100 overflow-hidden bg-white`}
      data-testid={dataTestId}
    >
      {headerContent && (
        <div
          className={`bg-${headerColor} px-4 py-4 md:px-6 md:py-2 md:h-[44px] text-center text-sm flex items-center justify-center ${
            headerColor === 'gray-50' ? 'border-b border-gray-100' : ''
          }`}
        >
          {headerContent}
        </div>
      )}
      <div
        className={`flex flex-col md:flex-row md:justify-between items-center px-6 py-4 ${
          props.variant === 'large' ? 'md:h-[141px]' : 'md:h-[80px]'
        }`}
      >
        <div className='flex items-center md:flex-row flex-col gap-4'>
          <AvatarNew
            fallbackIcon={userOrOrg === 'organization' ? 'building-04' : 'user-01'}
            url={avatarHref}
            size={props.variant === 'large' ? '2xl' : 'md'}
            withPadding={userOrOrg === 'organization'}
          />
          {/*{avatarHref && (
            <Avatar
              type='avatar'
              avatarUrl={avatarHref}
              size={props.variant === 'large' ? '2xl' : 'md'}
              additionalClassNames={
                props.variant !== 'large' ? 'w-12 h-12 text-lg md:w-10 md:h-10 md:text-md' : ''
              }
            />
          )}
          {!avatarHref && (
            <Avatar
              type='icon'
              icon='user-01'
              size={props.variant === 'large' ? '2xl' : 'md'}
              additionalClassNames={
                props.variant !== 'large' ? 'w-12 h-12 text-lg md:w-10 md:h-10 md:text-md' : ''
              }
            />
          )}*/}
          <div className='flex flex-col'>
            <div
              className={`flex flex-col ${props.variant !== 'large' ? 'md:flex-row' : ''} gap-2`}
            >
              {props.variant === 'large' && badgeRow}
              <div className='order-last md:order-none text-center md:text-base text-lg md:text-left items-center md:font-semibold flex flex-row gap-2 justify-center md:justify-start'>
                {contentTextHref && (
                  <a className='text-brand-600' href={contentTextHref} target='_blank'>
                    {content}
                  </a>
                )}
                {!contentTextHref && <div>{content}</div>}
                {contentIcon && <Icon type={contentIcon} size='20' />}
              </div>

              {props.variant !== 'large' && badgeRow}
            </div>
            {props.variant !== 'large' && (
              <div className='text-sm md:text-xs text-center md:text-left text-gray-400 mt-2 md:mt-0'>
                {props.contentSubText}
              </div>
            )}
            {props.variant === 'large' && (
              <a
                className='text-brand-600 text-sm md:text-xs text-center md:text-left mt-2 md:mt-0"'
                href={props.contentSubtextHref}
                target='_blank'
              >
                {contentSubText}
              </a>
            )}
          </div>
        </div>
        <div
          className={`flex flex-col items-center md:items-end justify-end ${
            props.variant === 'large' ? '' : 'justify-center'
          } md:ml-4`}
        >
          <div
            className={`whitespace-nowrap my-4 ${props.variant === 'large' ? 'md:my-6' : 'md:my-0'}`}
          >
            {buttonText && (
              <Button
                isDisabled={buttonDisabled}
                variant={buttonVariant || 'primary'}
                size='sm'
                additionalClassnames='min-w-[153px]'
                href={buttonHref}
                target={'_blank'}
                onPress={buttonOnClick}
              >
                {buttonText}
              </Button>
            )}
          </div>
          {props.variant === 'large' && props.footerText && (
            <div
              className={`min-w-[158px] text-xs text-gray-400 ${
                props.footerLinkText ? 'flex flex-col md:flex-row' : ''
              } text-center md:text-end md:mt-3 justify-between gap-2`}
            >
              {props.footerText}
              {props.footerLinkText && (props.footerLinkHref || props.footerLinkOnClick) && (
                <a
                  className='text-center md:text-left text-brand-100 underline cursor-pointer'
                  href={props.footerLinkHref}
                  onClick={props.footerLinkOnClick}
                >
                  {props.footerLinkText}
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
